/**
 * Pre/post session checklist templates.
 *
 * Pre-shift uses a 3-step wizard (support work vs therapy variants). Items are
 * soft-warn only — workers may proceed without completing. Post-session checks
 * run while the session is active (before check-out) and gate check-out plus
 * the progress-note draft pipeline.
 */

import type { ProgressNoteDraftFields } from './progress-note'
import type {
  ChecklistDiscipline,
  ChecklistPhase,
  SessionChecklist,
  SessionChecklistTemplate,
} from './types'

export type { ChecklistDiscipline, ChecklistPhase, SessionChecklist, SessionChecklistTemplate }

/** Wizard variant — OT/physio/speech/TA share the therapy template. */
export type PreShiftVariant = 'support_work' | 'therapy'

/** Which AHP-backed body placeholder a step shows when the API is unavailable. */
export type PreShiftStepBodyKind = 'profile_plan' | 'progress_notes' | 'travel_safety'

export type PostShiftStepBodyKind =
  | 'visited_places'
  | 'session_summary'
  | 'goal_progress'

export interface PreShiftStepTemplate {
  id: string
  heading: string
  subtext: string
  confirmLabel: string
  bodyKind: PreShiftStepBodyKind
}

export interface PreShiftWizardTemplate {
  variant: PreShiftVariant
  phase: 'pre_shift'
  title: string
  steps: PreShiftStepTemplate[]
}

export interface PostShiftStepTemplate {
  id: string
  heading: string
  subtext: string
  confirmLabel: string
  bodyKind: PostShiftStepBodyKind
  /** Progress note draft field populated by this segment's voice recording. */
  progressNoteField: keyof Pick<
    ProgressNoteDraftFields,
    'visitedPlaces' | 'sessionSummary' | 'goalProgress'
  >
}

export interface PostShiftWizardTemplate {
  variant: PreShiftVariant
  phase: 'post_shift'
  title: string
  steps: PostShiftStepTemplate[]
}

const SUPPORT_WORK_STEPS: PreShiftStepTemplate[] = [
  {
    id: 'sw_profile',
    heading: "Review the member's profile and support plan",
    subtext: 'Confirm you understand the member’s goals, risks, and current support plan before you leave.',
    confirmLabel: 'I have reviewed the member’s profile and support plan',
    bodyKind: 'profile_plan',
  },
  {
    id: 'sw_today_plan',
    heading: "Confirm today's plan for the session",
    subtext: 'Review what was done recently and what is planned for today based on recent progress notes.',
    confirmLabel: 'I have confirmed today’s session plan',
    bodyKind: 'progress_notes',
  },
  {
    id: 'sw_travel_safety',
    heading: 'Travel plans and safety considerations',
    subtext: 'Confirm a safe travel plan — where you are going, weather, and any safety considerations.',
    confirmLabel: 'I have a safe plan for travel today',
    bodyKind: 'travel_safety',
  },
]

const THERAPY_STEPS: PreShiftStepTemplate[] = [
  {
    id: 'th_profile',
    heading: "Review the member's profile and therapy plan",
    subtext: 'Confirm therapy goals, precautions, and the current plan before you leave.',
    confirmLabel: 'I have reviewed the member’s profile and therapy plan',
    bodyKind: 'profile_plan',
  },
  {
    id: 'th_session_equipment',
    heading: 'Confirm session plan and all equipment',
    subtext: 'Review today’s planned activities and ensure required equipment is ready.',
    confirmLabel: 'I have confirmed the session plan and equipment',
    bodyKind: 'progress_notes',
  },
  {
    id: 'th_travel_safety',
    heading: 'Travel plans and safety considerations',
    subtext: 'Confirm a safe travel plan — where you are going, weather, and any safety considerations.',
    confirmLabel: 'I have a safe plan for travel today',
    bodyKind: 'travel_safety',
  },
]

/** Unified pre-session wizard title (support work and therapy). */
export const PRE_SESSION_CHECKS_TITLE = 'Pre-Session Checks'

export const PRE_SHIFT_WIZARD_TEMPLATES: Record<PreShiftVariant, PreShiftWizardTemplate> = {
  support_work: {
    variant: 'support_work',
    phase: 'pre_shift',
    title: PRE_SESSION_CHECKS_TITLE,
    steps: SUPPORT_WORK_STEPS,
  },
  therapy: {
    variant: 'therapy',
    phase: 'pre_shift',
    title: PRE_SESSION_CHECKS_TITLE,
    steps: THERAPY_STEPS,
  },
}

/** Map checklist discipline to support-work vs therapy wizard variant. */
export function preShiftVariantFromDiscipline(discipline: ChecklistDiscipline): PreShiftVariant {
  return discipline === 'support_work' ? 'support_work' : 'therapy'
}

/** Resolve the 3-step pre-session checks wizard for a discipline. */
export function getPreShiftWizardTemplate(discipline: ChecklistDiscipline): PreShiftWizardTemplate {
  const variant = preShiftVariantFromDiscipline(discipline)
  return PRE_SHIFT_WIZARD_TEMPLATES[variant]
}

/** Legacy flat item list derived from wizard steps (response keys unchanged). */
export const PRE_SHIFT_CHECKLIST_TEMPLATES: Record<
  ChecklistDiscipline,
  SessionChecklistTemplate
> = {
  support_work: wizardToFlatTemplate('support_work', getPreShiftWizardTemplate('support_work')),
  ot: wizardToFlatTemplate('ot', getPreShiftWizardTemplate('ot')),
  physio: wizardToFlatTemplate('physio', getPreShiftWizardTemplate('physio')),
  speech: wizardToFlatTemplate('speech', getPreShiftWizardTemplate('speech')),
  ta: wizardToFlatTemplate('ta', getPreShiftWizardTemplate('ta')),
}

function wizardToFlatTemplate(
  discipline: ChecklistDiscipline,
  wizard: PreShiftWizardTemplate,
): SessionChecklistTemplate {
  return {
    discipline,
    phase: 'pre_shift',
    title: wizard.title,
    items: wizard.steps.map((step) => ({
      id: step.id,
      label: step.confirmLabel,
      required: false,
    })),
  }
}

export function getPreShiftTemplate(
  discipline: ChecklistDiscipline,
): SessionChecklistTemplate {
  return PRE_SHIFT_CHECKLIST_TEMPLATES[discipline]
}

export function createEmptyChecklist(
  discipline: ChecklistDiscipline,
  phase: ChecklistPhase,
): SessionChecklist {
  if (phase === 'post_shift') return createPostShiftEmptyChecklist(discipline)
  const wizard = getPreShiftWizardTemplate(discipline)
  const responses: Record<string, boolean> = {}
  for (const step of wizard.steps) {
    responses[step.id] = false
  }
  return { discipline, phase, responses, completedAt: null, progressNoteDraft: null }
}

/** True when every wizard step checkbox is checked. */
export function isPreShiftWizardComplete(
  discipline: ChecklistDiscipline,
  responses: Record<string, boolean>,
): boolean {
  const steps = getPreShiftWizardTemplate(discipline).steps
  return steps.every((step) => Boolean(responses[step.id]))
}

/** Unified post-session wizard title (support work and therapy). */
export const POST_SESSION_CHECKS_TITLE = 'Post-Session Checks'

const POST_SESSION_STEPS: PostShiftStepTemplate[] = [
  {
    id: 'post_visited_places',
    heading: 'Places visited',
    subtext: 'Record where you went with the member during this session.',
    confirmLabel: 'Places visited recorded',
    bodyKind: 'visited_places',
    progressNoteField: 'visitedPlaces',
  },
  {
    id: 'post_session_summary',
    heading: 'Session summary',
    subtext: 'Summarise what happened, support provided, and key observations.',
    confirmLabel: 'Session summary recorded',
    bodyKind: 'session_summary',
    progressNoteField: 'sessionSummary',
  },
  {
    id: 'post_goal_progress',
    heading: 'Goal progress',
    subtext: 'Describe progress toward agreed goals and anything to follow up.',
    confirmLabel: 'Goal progress recorded',
    bodyKind: 'goal_progress',
    progressNoteField: 'goalProgress',
  },
]

export const POST_SHIFT_WIZARD_TEMPLATES: Record<PreShiftVariant, PostShiftWizardTemplate> = {
  support_work: {
    variant: 'support_work',
    phase: 'post_shift',
    title: POST_SESSION_CHECKS_TITLE,
    steps: POST_SESSION_STEPS,
  },
  therapy: {
    variant: 'therapy',
    phase: 'post_shift',
    title: POST_SESSION_CHECKS_TITLE,
    steps: POST_SESSION_STEPS,
  },
}

export function getPostShiftWizardTemplate(discipline: ChecklistDiscipline): PostShiftWizardTemplate {
  const variant = preShiftVariantFromDiscipline(discipline)
  return POST_SHIFT_WIZARD_TEMPLATES[variant]
}

export function createPostShiftEmptyChecklist(discipline: ChecklistDiscipline): SessionChecklist {
  const wizard = getPostShiftWizardTemplate(discipline)
  const responses: Record<string, boolean> = {}
  for (const step of wizard.steps) {
    responses[step.id] = false
  }
  return { discipline, phase: 'post_shift', responses, completedAt: null, progressNoteDraft: null }
}

export function isPostShiftWizardComplete(
  discipline: ChecklistDiscipline,
  responses: Record<string, boolean>,
): boolean {
  const steps = getPostShiftWizardTemplate(discipline).steps
  return steps.every((step) => Boolean(responses[step.id]))
}
